import {
  Button,
  Menu,
  MenuItem,
  Stack,
  type SxProps,
  type Theme,
  useTheme,
} from "@mui/material";
import { CircularLoader, PriorityBadge } from "components";
import React from "react";
import type { PriorityType } from "shared";

export const PriorityDropdown = ({
  priority,
  isDisabled,
  isLoading = false,
  onSelect,
  endIcon,
  badgeContainerStyles,
}: {
  priority: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  onSelect?: (value: PriorityType) => void;
  endIcon?: React.ReactNode;
  /**
   * Overrides for the badge inside the trigger, merged over its defaults.
   * Lets a tight surface (the dispatch hover card) size the chip to its label
   * instead of the default minimum width.
   */
  badgeContainerStyles?: SxProps<Theme>;
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = async () => {
    setAnchorEl(null);
  };
  const handleUpdate = async (value: PriorityType) => {
    onSelect && onSelect(value);
    setAnchorEl(null);
  };
  const menu: Array<PriorityType> = ["critical", "high", "medium", "low"];
  return (
    <div>
      <Button
        disabled={isDisabled || isLoading}
        id="basic-button"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        sx={{
          px: 0,
          py: 0,
          color: theme.palette.lightBg.high,
          "&:hover": {
            "& .end-icon": {
              display: "flex",
            },
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{ position: "relative" }}
        >
          <PriorityBadge
            containerStyles={{ minWidth: "107px", ...badgeContainerStyles }}
            // backgroundColor={isDisabled ? theme.palette.neutral["020"] : "none"}
            type={priority}
          />
          <Stack
            className="end-icon"
            sx={{
              display: isLoading ? "flex" : "none",
              position: "absolute",
              right: 0,
              justifyContent: "center",
              alignItems: "center",
              pr: "4px",
              color: theme.palette.lightBg.high,
            }}
          >
            {isLoading ? (
              <CircularLoader
                size={16}
                sx={{
                  "&.MuiCircularProgress-root": {
                    color: "rgba(0, 0, 0, 0.2)",
                  },
                }}
              />
            ) : (
              endIcon
            )}
          </Stack>
        </Stack>
      </Button>
      <Menu
        sx={{ minWidth: "120px" }}
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        {menu.map((item: PriorityType) => (
          <MenuItem key={item} onClick={() => handleUpdate(item)}>
            <PriorityBadge type={item} />
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};
